import { useEffect, useState, type JSX } from 'react';
import { api, type ParishListItem, type PendingAccessRequest } from '../../api-client';
import { AuthLayout } from './AuthLayout';

interface Props {
  email: string;
  pendingRequests: PendingAccessRequest[];
}

/**
 * Rendered when an authenticated user has no parishes they can edit.
 *
 * If they have pending access requests, those are shown first with a cancel
 * option. The request form is always shown below so they can request access
 * to additional parishes.
 */
export function RequestAccessPage({ email, pendingRequests: initialPending }: Props): JSX.Element {
  const [parishes, setParishes] = useState<ParishListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [parishId, setParishId] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [pendingRequests, setPendingRequests] = useState<PendingAccessRequest[]>(initialPending);
  const [cancellingToken, setCancellingToken] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  useEffect(() => {
    api
      .listAllParishes()
      .then((res) => setParishes(res.parishes))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const submit = async (): Promise<void> => {
    if (!parishId || !reason.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.requestAccess(parishId, reason.trim());
      const selectedParish = parishes.find((p) => p.id === parishId);
      setPendingRequests((prev) => [
        {
          token: res.token,
          requester_email: email,
          parish_id: parishId,
          parish_name: selectedParish?.name ?? parishId,
          created_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
        },
        ...prev,
      ]);
      setParishId('');
      setReason('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  const cancel = async (token: string): Promise<void> => {
    setCancellingToken(token);
    setCancelError(null);
    try {
      await api.cancelAccessRequest(token);
      setPendingRequests((prev) => prev.filter((r) => r.token !== token));
    } catch (err) {
      setCancelError((err as Error).message);
    } finally {
      setCancellingToken(null);
    }
  };

  return (
    <AuthLayout>
      <div className="space-y-6">
        <header>
          <h2 className="text-2xl font-semibold text-ccaanz-black">Request access</h2>
          <p className="mt-2 text-sm text-ccaanz-grey">
            You're signed in as <strong className="text-ccaanz-black">{email}</strong>, but you're
            not yet authorised to edit any parish records. Pick a parish below and describe briefly
            why you need access — we'll email the parish's Senior Minister to approve your request.
          </p>
        </header>

        {pendingRequests.length > 0 && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-ccaanz-grey">
              Pending requests
            </h3>
            {cancelError && (
              <div className="rounded-control border border-ccaanz-red bg-ccaanz-red-light px-3 py-2 text-sm text-ccaanz-red">
                {cancelError}
              </div>
            )}
            {pendingRequests.map((req) => (
              <PendingRequestCard
                key={req.token}
                request={req}
                cancelling={cancellingToken === req.token}
                onCancel={() => void cancel(req.token)}
              />
            ))}
          </div>
        )}

        {error && (
          <div className="rounded-control border border-ccaanz-red bg-ccaanz-red-light px-3 py-2 text-sm text-ccaanz-red">
            {error}
          </div>
        )}

        <div className="panel space-y-5">
          <label className="block">
            <span className="input-label">Parish</span>
            {loading ? (
              <p className="mt-1 text-sm text-ccaanz-grey">Loading parishes…</p>
            ) : (
              <select
                value={parishId}
                onChange={(e) => setParishId(e.target.value)}
                className="input"
              >
                <option value="">— Select a parish —</option>
                {parishes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.church_type ? ` (${p.church_type})` : ''}
                  </option>
                ))}
              </select>
            )}
          </label>

          <label className="block">
            <span className="input-label">Why you need access</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. I'm the parish administrator at St Stephen's and I need to keep the personnel records up to date."
              rows={4}
              className="input"
            />
          </label>

          <div className="flex justify-end">
            <button
              type="button"
              disabled={submitting || !parishId || !reason.trim()}
              onClick={() => void submit()}
              className="btn btn-primary"
            >
              {submitting ? 'Sending…' : 'Send request'}
            </button>
          </div>
        </div>

        <p className="text-sm text-ccaanz-grey">
          Approval may take a day or two. If you don't hear back within a week, contact{' '}
          <a
            className="text-ccaanz-gold underline hover:text-ccaanz-gold-dark"
            href="mailto:registrar@confessinganglicans.nz"
          >
            registrar@confessinganglicans.nz
          </a>
          .
        </p>
      </div>
    </AuthLayout>
  );
}

interface CardProps {
  request: PendingAccessRequest;
  cancelling: boolean;
  onCancel: () => void;
}

function PendingRequestCard({ request, cancelling, onCancel }: CardProps): JSX.Element {
  const submitted = formatRelative(request.created_at);
  return (
    <div className="panel panel-section flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-medium text-ccaanz-black">{request.parish_name}</p>
        <p className="mt-0.5 text-xs text-ccaanz-grey">
          Submitted {submitted} · awaiting Senior Minister approval
        </p>
      </div>
      <button
        type="button"
        disabled={cancelling}
        onClick={onCancel}
        className="shrink-0 text-sm text-ccaanz-red underline hover:text-ccaanz-red-dark disabled:opacity-50"
      >
        {cancelling ? 'Cancelling…' : 'Cancel request'}
      </button>
    </div>
  );
}

function formatRelative(createdAt: string): string {
  const ms = Date.parse(createdAt.replace(' ', 'T') + 'Z');
  if (!Number.isFinite(ms)) return createdAt;
  const hours = Math.floor((Date.now() - ms) / 3_600_000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}
